import { useCallback, useEffect, useRef } from "react";
import { MapIconMarker } from "types/map";
import { useTheme } from "@mui/material";
import { registerNewTripFromMarker } from "redux/slices/shed/shedSlice";
import { useAppDispatch } from "redux/hooks";
import { getMarkerIconImageSrc } from "libraries/map";

type Props = {
  map: google.maps.Map | null; // Map instance passed from MapContainer
  marker: MapIconMarker;
};

const MapMarkerIcon = ({ map, marker }: Props) => {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const circleRef = useRef<google.maps.Circle | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const handleMarkerClick = useCallback(() => {
    console.log("[Map] Experience Icon marker click", marker.name);
    dispatch(registerNewTripFromMarker(marker));

    // Consider adding "registerReferenceExperience"
  }, [dispatch, marker]);

  useEffect(() => {
    if (!map) return;

    // Create the HTML content for the AdvancedMarkerElement
    if (!contentRef.current) {
      contentRef.current = document.createElement("div");
      contentRef.current.innerHTML = `<img style="width: 40px; height: 48px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));" src="${getMarkerIconImageSrc(
        marker
      )}" alt="${marker.name}" />`;
    }

    // Create marker only once
    if (!markerRef.current) {
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: {
          lat: marker.lat,
          lng: marker.lng,
        },
        content: contentRef.current,
        title: marker.name,
      });

      markerRef.current.addListener("click", handleMarkerClick);
    }

    if (!circleRef.current && marker.circleRadius > 0) {
      circleRef.current = new google.maps.Circle({
        map,
        radius: marker.circleRadius,
        strokeWeight: 2,
        strokeColor: theme.palette.success.main,
        fillColor: theme.palette.success.main,
        fillOpacity: 0.2,
        center: {
          lat: marker.lat,
          lng: marker.lng,
        },
      });
    }

    return () => {
      // Cleanup: Remove content and marker from the map
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current = null;
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [map]);

  return null;
};

export default MapMarkerIcon;
